<script setup lang="ts">
import { computed } from "vue";
import type { Advisory, Weather } from "../weather";
import AppIcon from "./AppIcon.vue";
import WeatherGlyph from "./WeatherGlyph.vue";

/** The forecast: where we are observing from, a weather word, and one sentence of evidence. */
const props = defineProps<{
  weather: Weather;
  advisories: ReadonlyArray<Advisory>;
  place: string;
  /** Local time at the observation point, e.g. "周三 19:06". */
  clock: string;
}>();

/**
 * Chinese text may wrap between any two characters, which can split a phrase in an ugly
 * place. Each clause is kept whole and lines break at the commas whenever there is room.
 */
const clauses = computed(() => props.weather.sentence.match(/[^，]+，?/g) ?? []);
</script>

<template>
  <section class="hero" aria-label="网络天气">
    <div class="hero__text">
      <p class="hero__place">
        <span>{{ place }}</span>
        <span v-if="clock" class="num">当地时间 {{ clock }}</span>
      </p>
      <!-- The slot keeps its height while the first observation loads, so nothing jumps. -->
      <div class="hero__word-slot">
        <Transition name="swap" mode="out-in">
          <h1 v-if="weather.word" id="hero-word" :key="weather.word" class="hero__word">
            <span class="visually-hidden">当前网络天气：</span>{{ weather.word }}
          </h1>
        </Transition>
      </div>
      <p class="hero__sentence">
        <span v-for="(clause, index) in clauses" :key="index">{{ clause }}</span>
      </p>
      <ul v-if="advisories.length" class="hero__advisories">
        <li v-for="item in advisories" :key="item.id" :class="`hero__advisory--${item.level}`">
          <AppIcon name="warning" :size="17" />
          <span>{{ item.text }}</span>
        </li>
      </ul>
    </div>
    <div class="hero__glyph">
      <Transition name="swap" mode="out-in">
        <WeatherGlyph
          :key="`${weather.condition}-${weather.night}`"
          :condition="weather.condition"
          :night="weather.night"
        />
      </Transition>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1.5rem clamp(1.5rem, 5vw, 4rem);
  align-items: start;
  padding-block: var(--hero-pad) clamp(2.5rem, 6vw, 4.5rem);
}
.hero__text { min-width: 0; }
.hero__place {
  display: flex;
  flex-wrap: wrap;
  gap: 0.15rem 0.9rem;
  color: var(--ink-3);
  font-size: 0.8125rem;
  letter-spacing: 0.06em;
}
.hero__place span:first-child { text-transform: uppercase; }
.hero__word-slot {
  --word-size: clamp(4rem, 13vw, 6.75rem);
  min-height: calc(var(--word-size) * 1.12);
  margin: 0.35rem 0 0.9rem;
}
.hero__word {
  font-family: var(--font-display);
  font-size: var(--word-size);
  font-weight: 600;
  line-height: 1.12;
  letter-spacing: 0.04em;
}
.hero__sentence {
  max-width: 34em;
  font-size: clamp(1.0625rem, 2.3vw, 1.3125rem);
  line-height: 1.65;
  text-wrap: balance;
}
.hero__sentence span { display: inline-block; }

.hero__advisories { display: grid; gap: 0.5rem; margin: 1.5rem 0 0; padding: 0; list-style: none; }
.hero__advisories li {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  max-width: 44em;
  color: var(--ink-2);
  font-size: 0.875rem;
  line-height: 1.55;
}
.hero__advisories svg { flex: 0 0 auto; margin-top: 0.2rem; color: var(--warn); }
.hero__advisory--critical svg { color: var(--bad); }

.hero__glyph { width: var(--glyph-size); color: var(--ink); --glyph-stroke: 2px; }

/* When the weather changes, the old word and glyph make way for the new ones. */
.swap-enter-active,
.swap-leave-active { transition: opacity 260ms ease, transform 260ms ease; }
.swap-enter-from { opacity: 0; transform: translateY(0.4rem); }
.swap-leave-to { opacity: 0; transform: translateY(-0.25rem); }

@media (prefers-reduced-motion: reduce) {
  .swap-enter-active,
  .swap-leave-active { transition: none; }
}

@media (max-width: 560px) {
  .hero { grid-template-columns: minmax(0, 1fr); }
  .hero__glyph { position: absolute; top: var(--hero-pad); right: 0; }
  .hero__place { max-width: calc(100% - 6.5rem); }
}
</style>
